// El navegador solo llama a nuestro servicio. Las firmas y la credencial AUESA viven allí.
import type { LatLng } from "../geo";

const BASE = "/api/live";
const TIMEOUT_MS = 15_000;

async function get<T>(path: string, signal?: AbortSignal): Promise<T> {
  const ctl = new AbortController();
  const timer = window.setTimeout(() => ctl.abort(), TIMEOUT_MS);
  const onAbort = (): void => ctl.abort();
  if (signal?.aborted) ctl.abort();
  signal?.addEventListener("abort", onAbort);
  try {
    const r = await fetch(`${BASE}${path}`, { signal: ctl.signal });
    if (!r.ok) throw new Error(`Tiempo real: HTTP ${r.status}`);
    return (await r.json()) as T;
  } finally {
    window.clearTimeout(timer);
    signal?.removeEventListener("abort", onAbort);
  }
}
async function catalog<T>(path: string, signal?: AbortSignal): Promise<T> {
  const envelope = await get<{ data?: T }>(`/catalog/${path}`, signal);
  if (envelope.data === undefined) throw new Error("Respuesta sin datos");
  return envelope.data;
}

export interface ApiStop {
  id: number;
  name: string;
  ll: LatLng;
}
export async function fetchStops(signal?: AbortSignal): Promise<Map<number, ApiStop>> {
  type Raw = { stops?: { id: string; name: string; lat: number; lon: number }[] }[];
  const raw = await catalog<Raw>("stops", signal);
  const out = new Map<number, ApiStop>();
  for (const op of raw) {
    for (const s of op.stops ?? []) {
      const id = Number(s.id);
      if (!Number.isSafeInteger(id) || !Number.isFinite(s.lat) || !Number.isFinite(s.lon)) continue;
      out.set(id, { id, name: s.name, ll: { lat: s.lat, lon: s.lon } });
    }
  }
  return out;
}
export interface ApiRoute {
  id: number;
  sense: string;
  destination: string;
  stops: { id: number; name: string; distance: number }[];
}
export async function fetchTopology(label: string, signal?: AbortSignal): Promise<ApiRoute[]> {
  const raw = await catalog<{ routes?: ApiRoute[] }>(
    `topology?label=${encodeURIComponent(label)}`,
    signal,
  );
  return raw.routes ?? [];
}
export interface ApiEstimation {
  line: string;
  destination: string;
  minutes: number;
  color?: string;
  occupancy?: string;
  observedAt?: number; // recepción de esta observación, nunca una fecha de posición GPS
  source?: "auesa";
}
export async function fetchEstimations(
  stopId: number,
  signal?: AbortSignal,
): Promise<ApiEstimation[]> {
  const response = await get<{ now: number; at: number; etas: ApiEstimation[] }>(
    `/stop/${stopId}`,
    signal,
  );
  if (
    !Number.isFinite(response.now) ||
    !Number.isFinite(response.at) ||
    !Array.isArray(response.etas)
  ) {
    throw new Error("Respuesta de llegadas inválida");
  }
  const offset = Date.now() - response.now;
  return response.etas.map((e) => ({ ...e, observedAt: (e.observedAt ?? response.at) + offset }));
}

// Los horarios absolutos requieren conocer su fecha: no los tratamos como minutos.
export function parseMinutes(s: string): number | null {
  const t = s.trim().toLowerCase();
  const m = /^(\d+)\s*min/.exec(t);
  if (m) return Number(m[1]);
  if (/^(llegando|ahora|inminente|en parada)/.test(t)) return 0;
  return null;
}
export interface ApiWarning {
  line: string;
  messages: string[];
  removedStops: number[];
}
export async function fetchWarnings(signal?: AbortSignal): Promise<ApiWarning[]> {
  type Raw = { line: { label: string }; messages?: string[]; removedStops?: { id: number }[] }[];
  const raw = await catalog<Raw>("warnings", signal);
  return raw.map((w) => ({
    line: w.line.label,
    messages: w.messages ?? [],
    removedStops: (w.removedStops ?? []).map((s) => Number(s.id)),
  }));
}
